/**
 * Copyright (c) 2017-present, Tomaruba, Inc.
 * All rights reserved.
 *
 */
import React from 'react'
import r from '../../../util/Renderer'
import { BaseLayout, ListViewLayout, LocalAreaLayout } from 'ui-library/dist/external/layouts'
import {
  DataTable,
  DataTableColumn as TD,
  DataTableHeader as TH,
  DataTableRow as TR,
  Detail,
  Dialog,
  DialogButton,
  DialogContent,
  DialogFooter,
  List,
  ListRow,
  ListRowItem,
  MdcChip,
  MdcChipSet,
  NavigationDrawer,
  Pagination,
  SearchCheckbox,
  SearchField,
  SearchOption,
  SelectOption,
  SelectOptions,
  TabHeader,
  TextArea,
  TextInput
} from 'ui-library/dist/external/components'
import { DateRangeField, Dropdown } from '../../atoms'
import AccommodationInvoiceAction from '../../../action/accommodation_invoice'
import Money from '../../unit/Money'
import ShowDate from '../../unit/Date'
import LocalDate from '../../../domain/type/Date'

function ListView(props) {
  const toolbar = { label: `請求一覧`, items: props.accountMenu }
  const pagination = props.pagination
  const searchFunction = params => {
    const pagination = props.pagination.merge({ page: params.page, per_page: params.perPage })
    AccommodationInvoiceAction.onSearch(props.search, pagination)
  }

  return (
    <React.Fragment>
      <NavigationDrawer toolbar={toolbar} navigations={props.navigations} />

      <BaseLayout>
        <LocalAreaLayout
          left={
            <SearchField
              name="search_keywords"
              value={props.search.search_keywords}
              onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)}
              onSearch={e => {
                AccommodationInvoiceAction.onSearch(props.search, pagination)
              }}
              searchOptions={[
                <SearchOption
                  key={1}
                  label={'宿泊施設'}
                  field={
                    <Dropdown
                      name="search_hotel_ids"
                      value={props.search.search_hotel_ids}
                      options={props.option_hotels}
                      isClearable={true}
                      isMulti={true}
                      searchable={true}
                      onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)}
                    />
                  }
                />,
                <SearchOption
                  key={2}
                  label={'チェックイン日'}
                  field={
                    <DateRangeField
                      className="searchDate"
                      startDateName="search_start_date"
                      endDateName="search_end_date"
                      startDate={props.search.search_start_date}
                      endDate={props.search.search_end_date}
                      onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)}
                    />
                  }
                />,
                <SearchOption
                  key={3}
                  label={'状態'}
                  field={
                    <ul>
                      <li>
                        <SearchCheckbox label={'請求前'} name={'before_invoice'} checked={props.search.before_invoice} onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)} />
                      </li>
                      <li>
                        <SearchCheckbox label={'請求中'} name={'invoicing'} checked={props.search.invoicing} onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)} />
                      </li>
                      <li>
                        <SearchCheckbox label={'支払済み'} name={'paid'} checked={props.search.paid} onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)} />
                      </li>
                      <li>
                        <SearchCheckbox label={'未払い'} name={'uncollectible'} checked={props.search.uncollectible} onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)} />
                      </li>
                      <li>
                        <SearchCheckbox label={'返金済み'} name={'refunded'} checked={props.search.refunded} onChange={e => AccommodationInvoiceAction.onEditSearchCondition(e)} />
                      </li>
                    </ul>
                  }
                />
              ]}
            />
          }
          bottom={ShowSearchCondition(props.search)}
        />

        <ListViewLayout>
          <DataTable
            headers={[
              <TH key={1} width="10%">
                状態
              </TH>,
              <TH key={2} width="18%">
                滞在期間
              </TH>,
              <TH key={3} width="20%">
                宿泊施設
              </TH>,
              <TH key={4} width="18%">
                氏名
              </TH>,
              <TH key={5} width="10%">
                種別
              </TH>,
              <TH key={6} width="15%" isNumber>
                金額
              </TH>,
              <TH key={7} width="10%">
                通貨
              </TH>
            ]}
            rows={props.invoices.map(invoice => (
              <TR key={invoice.id} onClick={e => AccommodationInvoiceAction.showDetailView(invoice.id)}>
                <TD>{Status(invoice)}</TD>
                <TD>
                  <ShowDate showYear={false}>{invoice.check_in}</ShowDate> ~<ShowDate showYear={false}>{invoice.check_out}</ShowDate>
                </TD>
                <TD>{invoice.room_name}</TD>
                <TD>{invoice.guest_name}</TD>
                <TD>{InvoiceType(invoice)}</TD>
                <TD isNumber>{Price(invoice)}</TD>
                <TD>{Currency(invoice)}</TD>
              </TR>
            ))}
          />
        </ListViewLayout>

        {r.renderIf(props.showDetail)(
          <Detail
            showHeader
            showFooter
            onDeactivate={AccommodationInvoiceAction.hideDetailView}
            primaryButton={PrimaryButton(props.reservation, props.invoice)}
            secondaryButton={SecondaryButton(props.invoice)}
            menuOptions={MenuOptions(props.invoice)}
          >
            <TabHeader onClose={AccommodationInvoiceAction.hideDetailView} tabs={[]} />
            <List>
              <ListRow key={1}>
                <ListRowItem width={'100%'} overline={`宿泊予約ID`} primaryText={ReservationId(props.reservation)} />
                <ListRowItem width={'50%'} overline={`チェックイン`} primaryItem={<ShowDate>{props.reservation.check_in}</ShowDate>} />
                <ListRowItem width={'50%'} overline={`チェックアウト`} primaryItem={<ShowDate>{props.reservation.check_out}</ShowDate>} />
                <ListRowItem width={'100%'} overline={`代表者氏名`} primaryText={props.reservation.name} />
                <ListRowItem width={'100%'} overline={`宿泊施設`} primaryText={props.reservation.stay} />
                <ListRowItem width={'50%'} overline={`宿泊費`} primaryText={AccommodationFee(props.reservation)} />
                <ListRowItem width={'50%'} overline={`OTA手数料`} primaryText={Commission(props.reservation)} />
                <ListRowItem width={'50%'} overline={`OTA`} primaryText={props.reservation.site_referer} />
                <ListRowItem width={'50%'} overline={`予約番号`} primaryText={props.reservation.reservation_code} />
              </ListRow>
              <ListRow key={2}>
                <ListRowItem width={'100%'} overline={`請求ID`} primaryText={props.invoice.id} />
                <ListRowItem width={'50%'} overline={`種別`} primaryText={InvoiceType(props.invoice)} />
                <ListRowItem width={'50%'} overline={`状態`} primaryText={Status(props.invoice)} />
                <ListRowItem width={'50%'} overline={`金額`} primaryItem={Price(props.invoice)} />
                <ListRowItem width={'50%'} overline={`通貨`} primaryText={Currency(props.invoice)} />
                <ListRowItem width={'100%'} overline={`顧客ID`} primaryText={Customerid(props.invoice)} />
                <ListRowItem width={'100%'} overline={`請求ID`} primaryText={Invoiceid(props.invoice)} />
                <ListRowItem
                  width={'100%'}
                  overline={`請求書URL`}
                  primaryItem={
                    <a href={`${InvoiceUrl(props.invoice)}`} target="blank" rel="noopener noreferrer">
                      {InvoiceUrl(props.invoice)}
                    </a>
                  }
                />
                <ListRowItem width={'100%'} overline={`備考`} primaryText={<span style={{ whiteSpace: 'pre-wrap' }}>{Note(props.invoice)}</span>} />
              </ListRow>
              {r.renderIf(props.invoice_details)(
                <ListRow key={3}>
                  {props.invoice_details.map((invoice_detail, index) => (
                    <React.Fragment>
                      <ListRowItem width={'100%'} overline={`明細#${index + 1}`} primaryText={invoice_detail.title} secondaryItem={InvoiceDetail(invoice_detail)} />
                    </React.Fragment>
                  ))}
                </ListRow>
              )}
            </List>
            <br />
            <br />
            <br />
          </Detail>
        )}
        <Dialog onClose={action => AccommodationInvoiceAction.onDialogClose(action, props.invoice, props.refund)} open={props.showDialog}>
          <DialogContent>
            <p>支払いの返金</p>
            <small>
              ※Stripe経由で返金処理を行います。
              <br />
              お客様の明細書に表示されるまで 5 日から 10 日かかることがあります。
            </small>
            <div style={{ margin: '8px 0' }}>
              <TextInput label="返金額" name={'amount'} value={props.refund.amount} onChange={e => AccommodationInvoiceAction.onEditRefund('amount', e.currentTarget.value)} />
            </div>
            <div style={{ margin: '8px 0' }}>
              <SelectOptions label="返金理由" value={props.refund.reason} onChange={e => AccommodationInvoiceAction.onEditRefund('reason', e.currentTarget.value)}>
                <SelectOption value="" />
                <SelectOption value="duplicate">重複支払い</SelectOption>
                <SelectOption value="fraudulent">カードの不正利用</SelectOption>
                <SelectOption value="requested_by_customer">顧客からの要求</SelectOption>
              </SelectOptions>
            </div>
            <div style={{ margin: '8px 0' }}>
              <TextArea label="詳細" name={'note'} value={props.refund.note} onChange={e => AccommodationInvoiceAction.onEditRefund('note', e.currentTarget.value)} />
            </div>
          </DialogContent>
          <DialogFooter>
            <DialogButton action="cancel">キャンセル</DialogButton>
            <DialogButton action="refund" isDefault disabled={!props.refund.canRefund()}>
              　返金処理　
            </DialogButton>
          </DialogFooter>
        </Dialog>
      </BaseLayout>
      <Pagination
        name="pagination"
        perPage={pagination.per_page}
        total={pagination.total}
        page={pagination.page}
        prev={e => searchFunction(e.value)}
        next={e => searchFunction(e.value)}
        perPageChanged={e => searchFunction(e.value)}
      />
    </React.Fragment>
  )
}

function ShowSearchCondition(search) {
  let labels = []
  if (search.search_keywords) {
    labels.push(`キーワード: ${search.search_keywords}`)
  }
  if (search.search_hotel_ids && search.search_hotel_ids.length > 0) {
    let hotelLabel = `宿泊施設: ${search.search_hotel_ids.length} 施設`
    labels.push(hotelLabel)
  }
  if (search.search_start_date || search.search_end_date) {
    let start = '(未設定)'
    let end = '(未設定)'
    if (search.search_start_date) {
      start = new LocalDate(search.search_start_date).toISODate()
    }
    if (search.search_end_date) {
      end = new LocalDate(search.search_end_date).toISODate()
    }
    let checkInLabel = `チェックイン日: ${start} ~ ${end}`
    labels.push(checkInLabel)
  }
  if (search.before_invoice || search.invoicing || search.paid || search.uncollectible || search.refunded) {
    let statusLabel = '状態: '
    let statusValues = []
    if (search.before_invoice) {
      statusValues.push('請求前')
    }
    if (search.invoicing) {
      statusValues.push('請求中')
    }
    if (search.paid) {
      statusValues.push('支払済み')
    }
    if (search.uncollectible) {
      statusValues.push('未払い')
    }
    if (search.refunded) {
      statusValues.push('返金済み')
    }

    const status = statusLabel + statusValues.join(', ')
    labels.push(status)
  }

  return (
    <MdcChipSet>
      {labels.map((label, index) => (
        <MdcChip id={index} label={label} />
      ))}
    </MdcChipSet>
  )
}

function ReservationId(reservation) {
  if (!reservation || !reservation.id) return ''
  return (
    <a href={`/reservation/${reservation.id}/detail`} target="_blank" rel="noopener noreferrer">
      {reservation.id}
    </a>
  )
}

function AccommodationFee(reservation) {
  if (!reservation || !reservation.accommodation_fee) return ''
  return <Money>{reservation.accommodation_fee}</Money>
}

function Commission(reservation) {
  if (!reservation || !reservation.commission) return ''
  return <Money>{reservation.commission}</Money>
}

function InvoiceType(invoice) {
  if (!invoice || !invoice.invoice_type) return ''
  return invoice.getInvoiceTypeLabel()
}

function Status(invoice) {
  if (!invoice || !invoice.status) return ''
  return invoice.getStatusLabel()
}

function Price(invoice) {
  if (!invoice || !invoice.total) return ''
  return <Money>{invoice.total}</Money>
}

function Currency(invoice) {
  if (!invoice || !invoice.currency) return ''
  return invoice.currency
}

function Customerid(invoice) {
  if (!invoice || !invoice.customerid) return ''
  return invoice.customerid
}

function Invoiceid(invoice) {
  if (!invoice || !invoice.invoiceid) return ''
  return invoice.invoiceid
}

function InvoiceUrl(invoice) {
  if (!invoice || !invoice.invoice_url) return ''
  return invoice.invoice_url
}

function Note(invoice) {
  if (!invoice || !invoice.note) return ''
  return invoice.note
}

function InvoiceDetail(invoice_detail) {
  if (!invoice_detail) return ''
  return (
    <React.Fragment>
      単価:<Money>{invoice_detail.unit_price}</Money>, 数量:<span>{invoice_detail.quantity}</span>, 合計: <Money>{invoice_detail.total}</Money>
    </React.Fragment>
  )
}

function PrimaryButton(reservation, invoice) {
  switch (invoice.status) {
    // 請求前→請求中
    case 'before_invoice':
      const noRefundDate = LocalDate.today().nextDay(30)
      const checkInDate = new Date(reservation.check_in)
      // 請求前から請求中にできるのはチェックイン30日前からの予約のみ
      if (checkInDate < noRefundDate) {
        return { label: '請求', onClick: e => AccommodationInvoiceAction.onUpdateInvoicing(invoice.id) }
      } else {
        return { label: '請求(注意)', onClick: e => AccommodationInvoiceAction.onUpdateInvoicing(invoice.id) }
      }
    // 請求中→入金待ち(支払い済み)
    case 'invoicing':
      break
    // 未払い→請求中
    case 'uncollectible':
      break
    case 'refunded':
      break
    default:
      break
  }
}

function SecondaryButton(invoice) {
  switch (invoice.status) {
    // 請求前→請求中
    case 'before_invoice':
      break
    // 請求中→入金待ち(支払い済み)
    case 'invoicing':
      break
    // 入金待ち(支払い済み)→入金確認済み
    case 'paid':
      return { label: '　返金　', onClick: e => AccommodationInvoiceAction.showRefundDialog(invoice.id) }
    // 未払い→請求中
    case 'uncollectible':
      break
    case 'refunded':
      break
    default:
      break
  }
}

function MenuOptions(invoice) {
  if (!invoice || !invoice.status) return null

  switch (invoice.status) {
    // 請求前
    case 'before_invoice':
      return MenuOptionsByBeforeInvoice(invoice)
    // 請求中
    case 'invoicing':
      return MenuOptionsByInvoicing(invoice)
    // 入金待ち(支払い済み)
    case 'paid':
      return MenuOptionsByPaid(invoice)
    // 未払い
    case 'uncollectible':
      return MenuOptionsByUncollectible(invoice)
    case 'refunded':
      break
    default:
      break
  }
}

function MenuOptionsByBeforeInvoice(invoice) {
  return [
    {
      label: '削除',
      onClick: () => {
        if (window.confirm('一度削除すると戻せません。\nこの操作はStripeのインボイスも無効に変更します。\n実行しますか？')) {
          AccommodationInvoiceAction.onRequestDelete(invoice.id)
        }
      }
    }
  ]
}

function MenuOptionsByInvoicing(invoice) {
  return [
    {
      label: '未払いにする',
      onClick: () => {
        if (window.confirm('状態を未払いに変更します。\nこの操作はStripeのインボイスも未払いに変更します。\nよろしいですか？')) {
          AccommodationInvoiceAction.onUpdateUncollectible(invoice.id)
        }
      }
    },
    {
      label: '支払済みにする',
      onClick: () => {
        if (window.confirm('状態を支払済みに変更します。\nこの操作によってStripeのインボイスを変更することはありません。\nよろしいですか？')) {
          AccommodationInvoiceAction.onUpdatePaid(invoice.id)
        }
      }
    },
    {
      label: '削除',
      onClick: () => {
        if (window.confirm('一度削除すると戻せません。\nこの操作はStripeのインボイスも無効に変更します。\n実行しますか？')) {
          AccommodationInvoiceAction.onRequestDelete(invoice.id)
        }
      }
    }
  ]
}

function MenuOptionsByPaid(invoice) {
  return null
}

function MenuOptionsByUncollectible(invoice) {
  return [
    {
      label: '削除',
      onClick: () => {
        if (window.confirm('一度削除すると戻せません。\nこの操作はStripeのインボイスも無効に変更します。\n実行しますか？')) {
          AccommodationInvoiceAction.onRequestDelete(invoice.id)
        }
      }
    }
  ]
}

export default ListView
